import { useEffect, useState } from 'react';
import Layout from '../components/Layout.jsx';
import { api } from '../api.js';

const fmt = (n) => '$ ' + new Intl.NumberFormat('es-CO').format(n ?? 0);

export default function Servicios() {
  const [turno, setTurno] = useState(null);
  const [servicios, setServicios] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [ventas, setVentas] = useState([]);
  const [base, setBase] = useState(null);
  const [unidad, setUnidad] = useState('');
  const [modificadores, setModificadores] = useState([]);
  const [cantidadExtra, setCantidadExtra] = useState({});
  const [observacion, setObservacion] = useState('');
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  const cargar = async () => {
    const [t, s, u] = await Promise.all([api.turnoAbierto(), api.servicios(), api.unidades()]);
    setTurno(t.turno);
    setServicios(s.servicios);
    setUnidades(u.unidades);
    if (t.turno) {
      const v = await api.ventasServicio(t.turno.id);
      setVentas(v.ventas);
    }
  };

  useEffect(() => { cargar(); }, []);

  const baseServ = servicios.filter((s) => !s.es_modificador);
  const modServ = servicios.filter((s) => s.es_modificador);
  const baseSel = servicios.find((s) => s.id === Number(base));

  const total = (() => {
    if (!baseSel) return 0;
    let t = baseSel.precio;
    for (const m of modificadores) {
      const ms = servicios.find((s) => s.id === m);
      const qty = Number(cantidadExtra[m] ?? 1);
      t += (ms?.precio ?? 0) * qty;
    }
    return t;
  })();

  const submit = async () => {
    if (!base) {
      setMsg('Elige un servicio principal.');
      return;
    }
    setLoading(true);
    setMsg('');
    try {
      // Vende el servicio base
      await api.crearVentaServicio({
        turno_id: turno?.id ?? null,
        servicio_id: Number(base),
        unidad_id: unidad ? Number(unidad) : null,
        cantidad: 1,
        monto: baseSel.precio,
        observacion: observacion || null,
      });
      // Vende cada modificador
      for (const m of modificadores) {
        const ms = servicios.find((s) => s.id === m);
        const qty = Number(cantidadExtra[m] ?? 1);
        await api.crearVentaServicio({
          turno_id: turno?.id ?? null,
          servicio_id: m,
          unidad_id: unidad ? Number(unidad) : null,
          cantidad: qty,
          monto: (ms?.precio ?? 0) * qty,
          observacion: 'Modificador de ' + baseSel.codigo,
        });
      }
      setMsg('✓ Servicio registrado · ' + fmt(total));
      setBase(null);
      setModificadores([]);
      setCantidadExtra({});
      setObservacion('');
      await cargar();
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const totalTurno = ventas.reduce((s, v) => s + (v.monto ?? 0), 0);

  return (
    <Layout>
      <div className="mb-8">
        <p className="eyebrow mb-2">Cuadre · servicios</p>
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight">Habitaciones</h1>
        <p className="text-muted mt-3 max-w-xl">Registra cada servicio que vendes — sencilla, junior, presidencial — más sus modificadores.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 card space-y-6">
          {!turno && <div className="rounded-2xl bg-bone p-4 text-sm text-muted">Abre un turno antes de registrar servicios.</div>}

          <div>
            <p className="eyebrow mb-3">1 · Tipo de servicio</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {baseServ.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setBase(s.id)}
                  className={`p-4 rounded-2xl text-left transition-all duration-200 ${
                    base === s.id
                      ? 'bg-ink text-paper shadow-[0_8px_20px_-8px_rgba(27,42,78,0.5)]'
                      : 'bg-bone text-ink hover:bg-line'
                  }`}
                >
                  <div className="font-semibold tracking-tight text-sm">{s.nombre}</div>
                  <div className="font-mono text-[11px] mt-2 opacity-80">{s.precio > 0 ? fmt(s.precio) : '—'}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <p className="eyebrow mb-3">2 · Habitación</p>
            <select className="input" value={unidad} onChange={(e) => setUnidad(e.target.value)}>
              <option value="">— elegir —</option>
              {unidades.filter((u) => u.tipo === 'habitacion').map((u) => (
                <option key={u.id} value={u.id}>{u.codigo} · {u.nombre}</option>
              ))}
            </select>
          </div>

          <div>
            <p className="eyebrow mb-3">3 · Adicionales (opcional)</p>
            <div className="space-y-2">
              {modServ.map((m) => {
                const checked = modificadores.includes(m.id);
                return (
                  <label key={m.id} className={`flex items-center justify-between gap-3 p-3 rounded-2xl cursor-pointer transition ${checked ? 'bg-lime/30 border border-lime-deep/30' : 'bg-bone hover:bg-line'}`}>
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => {
                          if (e.target.checked) setModificadores([...modificadores, m.id]);
                          else setModificadores(modificadores.filter((x) => x !== m.id));
                        }}
                        className="w-5 h-5 accent-ink"
                      />
                      <div>
                        <div className="font-medium tracking-tight text-sm">{m.nombre}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">{m.precio > 0 ? fmt(m.precio) + ' c/u' : 'Variable'}</div>
                      </div>
                    </div>
                    {checked && (
                      <input
                        type="number"
                        min={1}
                        value={cantidadExtra[m.id] ?? 1}
                        onChange={(e) => setCantidadExtra({ ...cantidadExtra, [m.id]: e.target.value })}
                        className="w-16 px-2 py-1 rounded-lg border border-line text-center font-mono text-sm"
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </div>

          <div>
            <label className="label">Observación (opcional)</label>
            <input className="input" value={observacion} onChange={(e) => setObservacion(e.target.value)} placeholder="ej. cliente preguntó algo, retraso, etc." />
          </div>

          <div className="card-lime p-4 flex items-center justify-between">
            <span className="eyebrow">Total servicio</span>
            <span className="text-2xl font-semibold tracking-tight font-mono">{fmt(total)}</span>
          </div>

          {msg && (
            <div className={`rounded-2xl px-4 py-3 text-sm font-mono ${msg.startsWith('✓') ? 'bg-lime/30 text-ink' : 'bg-red-50 text-red-800 border border-red-200'}`}>{msg}</div>
          )}

          <button onClick={submit} className="btn-primary w-full" disabled={loading || !turno}>
            {loading ? 'Registrando…' : 'Registrar servicio →'}
          </button>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="card-ink relative overflow-hidden grain">
            <div className="absolute -right-12 -top-12 w-48 h-48 rounded-full opacity-20 blur-3xl pointer-events-none" style={{ background: 'radial-gradient(closest-side, #C8E847, transparent)' }} />
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-lime mb-3 relative">Servicios del turno</p>
            <div className="text-4xl font-bold tracking-tight relative">{fmt(totalTurno)}</div>
            <div className="font-mono text-xs text-paper/70 mt-2 relative">{ventas.length} registros</div>
          </div>

          <div className="card">
            <p className="eyebrow mb-4">Bitácora</p>
            {ventas.length === 0 && <p className="text-muted text-sm">Aún no has registrado servicios en este turno.</p>}
            <div className="space-y-2 max-h-[480px] overflow-y-auto">
              {ventas.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-2 py-2 px-3 rounded-2xl hover:bg-bone transition">
                  <div className="min-w-0">
                    <div className="font-medium tracking-tight text-sm truncate">{v.servicio}</div>
                    <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mt-0.5">
                      {v.unidad ?? '—'} · {v.usuario}
                    </div>
                  </div>
                  <div className="font-mono text-sm shrink-0">{fmt(v.monto)}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
